/**
 * Seller — Mi Cartera (lista de los clientes asignados al vendedor actual).
 * Solo lectura desde acá. Para crear nuevo cliente usar el botón "+" inline en
 * Tomar Pedido, que también auto-asigna al cliente al vendedor.
 */
import { useEffect, useState } from "react";
import { api } from "../../App";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Users, Phone, MapPin, Search } from "lucide-react";
import { PageLoader } from "../../components/Spinner";
import { EmptyState } from "../../components/EmptyState";

export default function SellerCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/seller/customers");
        setCustomers(data || []);
      } catch {
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <PageLoader label="Cargando clientes…" testid="seller-customers-loader" />;

  const filtered = customers.filter((c) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return (
      (c.nombre_facturacion || "").toLowerCase().includes(t) ||
      (c.nombre_fantasia || "").toLowerCase().includes(t) ||
      (c.phone || "").toLowerCase().includes(t)
    );
  });

  return (
    <div data-testid="seller-customers-page">
      <div className="mb-5">
        <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Mi Cartera
        </h2>
        <p className="text-slate-500 text-sm mt-0.5">
          {customers.length} cliente{customers.length === 1 ? "" : "s"} asignado{customers.length === 1 ? "" : "s"} a ti
        </p>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre o teléfono…"
          className="pl-9 rounded-sm"
          data-testid="seller-customers-search"
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title={q ? "Sin resultados" : "Sin clientes asignados"}
          description={
            q
              ? "Probá con otro término de búsqueda."
              : "Cuando tu admin te asigne clientes, aparecerán acá. Mientras tanto, podés crear uno nuevo desde 'Tomar Pedido'."
          }
          testid="seller-customers-empty"
        />
      ) : (
        <div className="space-y-2.5">
          {filtered.map((c) => (
            <Card key={c.id} className="bg-white border border-slate-200 rounded-sm p-3" data-testid="seller-customer-card">
              <div className="font-semibold text-slate-900">{c.nombre_facturacion}</div>
              {c.nombre_fantasia && (
                <div className="text-xs text-slate-500 -mt-0.5">{c.nombre_fantasia}</div>
              )}
              <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                {c.phone && (
                  <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-[#002FA7]">
                    <Phone className="h-3 w-3" />
                    {c.phone}
                  </a>
                )}
                {c.address && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    <span className="truncate max-w-[260px]">{c.address}</span>
                  </div>
                )}
              </div>
              {c.notes && (
                <p className="mt-1.5 text-[11px] text-slate-500 italic line-clamp-2">{c.notes}</p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
